import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, Loader2, Sparkles } from 'lucide-react';
import html2canvas from 'html2canvas';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// The 15 sticker slugs that exist as PNGs in /public/stickers/
// Names match files generated by /app/backend/scripts/generate_stickers.py
const STICKER_POOL = [
  { slug: 'goofy-panda',      name: 'Goofy Panda' },
  { slug: 'ninja-frog',       name: 'Ninja Frog' },
  { slug: 'pizza-sloth',      name: 'Pizza Sloth' },
  { slug: 'astronaut-cat',    name: 'Astronaut Cat' },
  { slug: 'taco-llama',       name: 'Taco Llama' },
  { slug: 'robot-bunny',      name: 'Robot Bunny' },
  { slug: 'dino-dancer',      name: 'Dino Dancer' },
  { slug: 'rocket-hedgehog',  name: 'Rocket Hedgehog' },
  { slug: 'skateboard-sloth', name: 'Skateboard Sloth' },
  { slug: 'wizard-owl',       name: 'Wizard Owl' },
  { slug: 'surfer-otter',     name: 'Surfer Otter' },
  { slug: 'disco-giraffe',    name: 'Disco Giraffe' },
  { slug: 'karate-fox',       name: 'Karate Fox' },
  { slug: 'dj-penguin',       name: 'DJ Penguin' },
  { slug: 'soccer-hippo',     name: 'Soccer Hippo' },
];

// Stable string hash → non-negative int.
const hashStr = (str) => {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return Math.abs(h);
};

// ISO week number for any date (so kids get a new buddy every week).
const isoWeekNumber = (d) => {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  // Thursday of current week decides the year.
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
};

// Pick a sticker deterministically from (participant_id + event_week).
const pickSticker = (participantId, eventDate) => {
  let week = 0;
  let year = new Date().getFullYear();
  if (eventDate) {
    const d = new Date(eventDate);
    if (!isNaN(d.getTime())) {
      week = isoWeekNumber(d);
      year = d.getFullYear();
    }
  } else {
    const now = new Date();
    week = isoWeekNumber(now);
    year = now.getFullYear();
  }
  const seed = `${participantId || 'anon'}::${year}-W${week}`;
  return STICKER_POOL[hashStr(seed) % STICKER_POOL.length];
};

// Soft pastel cards under the sticker — also deterministic per kid.
const PASTEL_BGS = [
  { bg: '#fef3c7', accent: '#f59e0b' }, // amber cream
  { bg: '#dbeafe', accent: '#3b82f6' }, // sky
  { bg: '#fce7f3', accent: '#ec4899' }, // pink
  { bg: '#dcfce7', accent: '#22c55e' }, // mint
  { bg: '#ede9fe', accent: '#8b5cf6' }, // lavender
  { bg: '#ffedd5', accent: '#f97316' }, // peach
];

const pickPastel = (participantId) =>
  PASTEL_BGS[hashStr(`bg::${participantId || 'anon'}`) % PASTEL_BGS.length];

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return iso;
  }
};

export default function StickerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [participantId, setParticipantId] = useState('');
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const pid = searchParams.get('participant_id');
    if (pid) {
      setParticipantId(pid);
      loadCert(pid);
    } else {
      const t = setTimeout(() => setLoading(false), 0);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadCert = async (pid) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/certificate/${pid}`);
      setCertificate(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not find that participant ID.');
    } finally {
      setLoading(false);
    }
  };

  const handleLookup = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!participantId.trim()) return;
    navigate(`/sticker?participant_id=${participantId.trim()}`);
    loadCert(participantId.trim());
  };

  const downloadPNG = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      // Wait for any pending image decode + fonts so html2canvas snapshots cleanly.
      if (document?.fonts?.ready) await document.fonts.ready;
      // Give the sticker <img> a brief moment to finish painting on first render.
      await new Promise((r) => setTimeout(r, 150));

      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        allowTaint: false,
        logging: false,
      });
      const dataUrl = canvas.toDataURL('image/png');
      const safeName = (certificate?.participant_name || 'sticker').replace(/[^a-z0-9]+/gi, '_');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `BrainGym-Sticker-${safeName}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Sticker download failed', err);
      setError('Could not generate the sticker image. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // ── Render: loading ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0f172a]" />
      </div>
    );
  }

  // ── Render: lookup form (no participant_id in URL) ────────────────────────
  if (!certificate) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col">
        <header className="px-4 sm:px-6 py-4 flex items-center justify-between bg-white border-b border-slate-200">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm font-semibold text-[#0f172a]"
            data-testid="sticker-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </header>
        <main className="flex-1 flex items-center justify-center p-6">
          <form
            onSubmit={handleLookup}
            className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-slate-200 p-8"
            data-testid="sticker-lookup-form"
          >
            <div className="flex items-center gap-2 mb-2 text-[#f59e0b]">
              <Sparkles className="w-5 h-5" />
              <span className="text-xs uppercase tracking-widest font-bold">Sticker Studio</span>
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Grab your weekly sticker
            </h1>
            <p className="text-sm text-slate-500 mb-6">
              Enter your Participant ID to see this week&apos;s surprise sticker buddy.
            </p>
            <input
              type="text"
              value={participantId}
              onChange={(e) => setParticipantId(e.target.value)}
              placeholder="OLY-2026-XXXXX"
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#f59e0b] focus:outline-none font-mono text-sm"
              data-testid="sticker-pid-input"
            />
            {error && <div className="mt-3 text-sm text-rose-600" data-testid="sticker-error">{error}</div>}
            <button
              type="submit"
              className="mt-4 w-full bg-[#0f172a] text-white font-bold py-3 rounded-xl hover:bg-[#1e293b] transition-colors"
              data-testid="sticker-lookup-submit"
            >
              Show my sticker
            </button>
          </form>
        </main>
      </div>
    );
  }

  // ── Render: sticker card ──────────────────────────────────────────────────
  const sticker = pickSticker(participantId, certificate.event_date);
  const pastel = pickPastel(participantId);
  const stickerUrl = `/stickers/${sticker.slug}.png`;
  const eventDateLabel = formatDate(certificate.event_date);

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col">
      <header className="px-4 sm:px-6 py-4 flex items-center justify-between bg-white border-b border-slate-200">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-semibold text-[#0f172a]"
          data-testid="sticker-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={downloadPNG}
          disabled={downloading}
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-[#0f172a] text-white disabled:opacity-60"
          data-testid="sticker-download-btn"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download Your Sticker
        </button>
      </header>

      <main className="flex-1 px-4 sm:px-6 py-8 flex flex-col items-center">
        <div className="flex items-center gap-2 mb-3 text-[#f59e0b]">
          <Sparkles className="w-4 h-4" />
          <span className="text-xs uppercase tracking-widest font-bold">This week&apos;s surprise buddy</span>
          <Sparkles className="w-4 h-4" />
        </div>
        <h1
          className="text-2xl sm:text-3xl font-bold text-[#0f172a] mb-1 text-center"
          style={{ fontFamily: 'Fredoka, sans-serif' }}
        >
          Meet your sticker, {certificate.participant_name}!
        </h1>
        <p className="text-sm text-slate-500 mb-6 text-center">
          A fresh buddy is picked just for you every week. Tap the Download button to save the PNG.
        </p>

        {/* THE STICKER — captured by html2canvas */}
        <div
          ref={cardRef}
          data-testid="sticker-card"
          style={{
            width: 520,
            maxWidth: '100%',
            aspectRatio: '1 / 1',
            background: pastel.bg,
            border: `8px solid ${pastel.accent}`,
            borderRadius: 36,
            padding: 24,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 20px 50px rgba(15,23,42,0.18)',
            fontFamily: 'Fredoka, "Helvetica Neue", Helvetica, Arial, sans-serif',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Top label */}
          <div style={{
            fontSize: 13, fontWeight: 800, letterSpacing: '3px',
            color: pastel.accent, textTransform: 'uppercase',
          }}>
            Brain Gym Jr. · Sticker
          </div>

          {/* The illustration itself */}
          <img
            src={stickerUrl}
            alt={sticker.name}
            crossOrigin="anonymous"
            style={{
              width: '78%',
              maxWidth: 360,
              aspectRatio: '1 / 1',
              objectFit: 'contain',
              flexShrink: 0,
              filter: 'drop-shadow(0 8px 18px rgba(15,23,42,0.18))',
            }}
          />

          {/* Footer block: name + buddy name + date */}
          <div style={{ textAlign: 'center', width: '100%' }}>
            <div style={{
              fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1.05,
              wordBreak: 'break-word',
            }}>
              {certificate.participant_name}
            </div>
            <div style={{
              marginTop: 4, fontSize: 14, fontWeight: 600, color: pastel.accent,
              letterSpacing: '1px', textTransform: 'uppercase',
            }}>
              & {sticker.name}
            </div>
            {eventDateLabel && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', letterSpacing: '0.5px' }}>
                {certificate.event_name || 'Brain Gym Jr.'} · {eventDateLabel}
              </div>
            )}
          </div>
        </div>

        {/* CTA below */}
        <button
          onClick={downloadPNG}
          disabled={downloading}
          className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0f172a] text-white font-bold shadow-lg hover:bg-[#1e293b] transition-colors disabled:opacity-60"
          data-testid="sticker-download-cta"
        >
          {downloading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
          Download Your Sticker
        </button>

        <p className="mt-4 text-xs text-slate-400 text-center max-w-xs">
          Print it as a die-cut sticker, set it as your profile pic, or share with friends.
        </p>
      </main>
    </div>
  );
}
